import type { Nullable, PriceId, ProductKind } from '@iheartjane/types';
import pickBy from 'lodash-es/pickBy';

/**
 * A product's per-weight potency entry. Deliberately looser than
 * `InventoryPotency` from `@jane/shared/types`: `price_id` is a plain string so
 * that callers whose product type widens it — the monolith's converted
 * io-ts models, for one — still satisfy this shape. All we do with it is
 * compare it against the selected weight.
 */
interface WeightPotency {
  cbd_potency?: Nullable<number>;
  price_id?: Nullable<string>;
  tac_potency?: Nullable<number>;
  thc_potency?: Nullable<number>;
  thca_potency?: Nullable<number>;
}

/**
 * The minimal product shape these helpers read. Declared structurally rather
 * than as a union of the concrete product types (`MenuProduct`, `Product`,
 * `AlgoliaProduct`, `MaterializedMenuProduct`, ...) so this module does not
 * depend on those very large types, and so callers can pass anything that
 * carries potency fields.
 *
 * NB: every field is optional because no single product type carries all of
 * them — the camelCase potencies come from the monolith serializers and the
 * snake_case ones from the menu/search payloads. The search index also sends
 * `percent_*` as strings, hence the widened value types.
 */
export interface PotencyProduct {
  amount?: Nullable<string>;
  dosage?: Nullable<string>;
  inventory_potencies?: readonly WeightPotency[];
  kind: ProductKind;
  net_weight_grams?: Nullable<number>;
  percent_cbd?: Nullable<number | string>;
  percent_tac?: Nullable<number | string>;
  percent_thc?: Nullable<number | string>;
  percent_thca?: Nullable<number | string>;
  percentCBDA?: Nullable<number>;
  percentTAC?: Nullable<number>;
  percentTHCA?: Nullable<number>;
  primaryPercentCBD?: Nullable<number>;
  primaryPercentTHC?: Nullable<number>;
  quantity_units?: Nullable<string>;
  quantity_value?: Nullable<number>;
  root_subtype?: Nullable<string>;
}

type PrimaryPotenciesReturn = Partial<{
  percentCbd: number;
  percentTac: number;
  percentThc: number;
  percentThca: number;
}>;

type GetLabResultsReturn = Partial<{
  percentCbd: number;
  percentCbda: number;
  percentTac: number;
  percentThc: number;
  percentThca: number;
}>;

type GetDosageAmountPackInfoReturn = Partial<{
  dosage: string;
  packTotal: string;
  productAmount: string;
}>;

type GetPotencyInfoReturn = Partial<
  ReturnType<typeof getLabResults> & ReturnType<typeof getDosageAmountPackInfo>
> | null;

const nonCannabisKinds = ['gear', 'merch'];

export const formatPotency = (potency?: number | string) =>
  potency ? parseFloat(parseFloat(potency.toString()).toFixed(2)) : null;

export const primaryPotencies = (
  product: PotencyProduct,
  selectedWeight?: PriceId
): PrimaryPotenciesReturn => {
  const inventoryPotency = product.inventory_potencies?.find(
    (potency) => potency.price_id === selectedWeight
  );

  const percentTac =
    inventoryPotency?.tac_potency || product.percentTAC || product.percent_tac;
  const percentThc =
    inventoryPotency?.thc_potency ||
    product.primaryPercentTHC ||
    product.percent_thc;
  const percentThca =
    inventoryPotency?.thca_potency ||
    product.percentTHCA ||
    product.percent_thca;
  const percentCbd =
    inventoryPotency?.cbd_potency ||
    product.primaryPercentCBD ||
    product.percent_cbd;

  return pickBy({ percentTac, percentThc, percentThca, percentCbd }, Boolean);
};

export const getPackTotal = (product: PotencyProduct) => {
  let { net_weight_grams = 0, quantity_value = 0 } = product;
  net_weight_grams ??= 0;
  quantity_value ??= 0;

  const shouldAppendPackTotal =
    ['pre-roll', 'vape'].includes(product.kind) &&
    product.root_subtype?.toLowerCase()?.includes('packs');

  if (!shouldAppendPackTotal) {
    return;
  }

  if (product.quantity_units && quantity_value > 0) {
    const standardizedUnits = product.quantity_units.replace('_', ' ');
    return `${quantity_value}${standardizedUnits}`;
  }

  if (net_weight_grams > 0) {
    return `${net_weight_grams}g`;
  }

  return;
};

export const getLabResults = (
  product: PotencyProduct,
  selectedWeight?: PriceId
): GetLabResultsReturn => {
  const { percentTac, percentThc, percentThca, percentCbd } = primaryPotencies(
    product,
    selectedWeight
  );

  return pickBy(
    {
      percentCbd,
      percentCbda: product.percentCBDA,
      percentTac,
      percentThc,
      percentThca,
    },
    Boolean
  );
};

export const getDosageAmountPackInfo = (
  product: PotencyProduct
): GetDosageAmountPackInfoReturn => {
  const { amount, dosage } = product;

  if (!dosage && !amount) {
    return {};
  }

  const packTotal = getPackTotal(product);

  return pickBy(
    {
      dosage,
      packTotal,
      productAmount: amount,
    },
    Boolean
  );
};

export const getPotencyInfo = (
  product: PotencyProduct,
  selectedWeight?: PriceId
): GetPotencyInfoReturn => {
  if (nonCannabisKinds.includes(product.kind)) {
    return null;
  }

  const labResults = getLabResults(product, selectedWeight);
  const dosageAmountInfo = getDosageAmountPackInfo(product);

  return { ...dosageAmountInfo, ...labResults };
};
